import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  FileText, 
  Download,
  Filter,
  Calendar,
  Loader2,
  Table,
  FileSpreadsheet,
  Clock,
  Share2,
  MessageCircle,
  FileDown,
  DollarSign,
  Wrench
} from 'lucide-react';
import { authHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { PdfActionsModal } from '../components/PdfActionsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports({ userRole }) {
  const { selectedTenantId } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [pdfModal, setPdfModal] = useState(null); // { url, filename, title }
  
  // Verificar se usuário pode ver faturamento (apenas admin e gestor)
  const canSeeBilling = ['admin', 'gestor'].includes(userRole);
  
  // Filtros
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    status: 'all',
    operation_type: '',
    location_id: '',
    machine_id: '',
    operator_id: '',
    tipo_os: ''
  });
  
  // Filtros de manutenção
  const [maintFilters, setMaintFilters] = useState({
    date_from: '',
    date_to: '',
    machine_id: '',
    technician_id: '',
    maintenance_type: '',
    status: ''
  });
  
  // Valor da hora para cálculo de faturamento
  const [hourlyRate, setHourlyRate] = useState('');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const headers = authHeaders();

      const [locRes, machRes, opRes, typesRes, techRes] = await Promise.all([
        fetch(`${API_URL}/api/locations`, { headers }),
        fetch(`${API_URL}/api/machines`, { headers }),
        fetch(`${API_URL}/api/operators`, { headers }),
        fetch(`${API_URL}/api/operation-types`, { headers }),
        fetch(`${API_URL}/api/technicians`, { headers })
      ]);

      if (locRes.ok) setLocations(await locRes.json());
      if (machRes.ok) setMachines(await machRes.json());
      if (opRes.ok) setOperators(await opRes.json());
      if (typesRes.ok) {
        const data = await typesRes.json();
        setOperationTypes(data.types || []);
      }
      if (techRes.ok) setTechnicians(await techRes.json());
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    }
  };

  const generateReport = async () => {
    if (!filters.date_from || !filters.date_to) {
      toast.error('Selecione o período do relatório');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/work-orders`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(filters)
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        toast.success(`Relatório gerado: ${data.summary.total_records} registros`);
      } else {
        toast.error('Erro ao gerar relatório');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData || reportData.data.length === 0) {
      toast.error('Gere o relatório primeiro');
      return;
    }

    // Gerar CSV diretamente dos dados já carregados
    const headers = ['O.S. Nº', 'Data', 'Local', 'Tipo Operação', 'Máquina', 'Operador', 'Hor. Inicial', 'Hor. Final', 'Horas', 'Status'];
    
    const rows = reportData.data.map(row => [
      row.order_number || '',
      row.date || '',
      row.location || '',
      row.operation_type || '',
      row.machine || '',
      row.operator || '',
      row.start_hour_meter || '',
      row.end_hour_meter || '',
      row.hours_worked || '',
      row.status || ''
    ]);

    // Adicionar linha de totais
    rows.push(['', '', '', '', '', '', '', 'TOTAL:', reportData.summary.total_hours, '']);

    // Montar conteúdo CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\r\n');

    // Usar Data URI para download
    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent('\ufeff' + csvContent);
    const filename = `relatorio_os_${filters.date_from}_${filters.date_to}.csv`;
    
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Download concluído!');
  };
  
  // Função para abrir CSV em nova aba (alternativa)
  const getCSVUrl = () => {
    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to
    });
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.operation_type) params.append('operation_type', filters.operation_type);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.machine_id) params.append('machine_id', filters.machine_id);
    if (filters.operator_id) params.append('operator_id', filters.operator_id);
    if (filters.tipo_os) params.append('tipo_os', filters.tipo_os);
    return `${API_URL}/api/reports/export-csv?${params.toString()}`;
  };

  // URL do PDF
  const getPDFUrl = () => {
    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to
    });
    if (selectedTenantId) params.append('tenant_id', selectedTenantId);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.operation_type) params.append('operation_type', filters.operation_type);
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.machine_id) params.append('machine_id', filters.machine_id);
    if (filters.operator_id) params.append('operator_id', filters.operator_id);
    if (filters.tipo_os) params.append('tipo_os', filters.tipo_os);
    if (hourlyRate && parseFloat(hourlyRate) > 0) params.append('hourly_rate', hourlyRate);
    // Nome do usuário logado para assinatura
    const userName = localStorage.getItem('userName') || 'Responsável';
    params.append('issued_by_name', userName);
    return `${API_URL}/api/reports/export-pdf?${params.toString()}`;
  };

  // Compartilhar link CSV via WhatsApp
  const shareCSVLink = () => {
    if (!reportData) {
      toast.error('Gere o relatório primeiro');
      return;
    }
    const csvUrl = encodeURIComponent(getCSVUrl());
    const message = `📊 *Relatório de O.S. - Impulso Digital*%0A%0A📅 Período: ${formatDate(filters.date_from)} a ${formatDate(filters.date_to)}%0A📈 Total: ${reportData.summary.total_hours}h (${reportData.summary.total_records} registros)%0A%0A📥 Baixe o relatório:%0A${csvUrl}`;
    window.open(`https://wa.me/?text=${message}`, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  // Compartilhar resumo via WhatsApp
  const shareReportSummary = () => {
    if (!reportData) {
      toast.error('Gere o relatório primeiro');
      return;
    }
    
    // Construir resumo
    let summary = `📊 *RELATÓRIO DE ORDENS DE SERVIÇO*%0A`;
    summary += `🏢 Impulso Digital%0A%0A`;
    summary += `📅 *Período:* ${formatDate(filters.date_from)} a ${formatDate(filters.date_to)}%0A%0A`;
    summary += `📈 *RESUMO:*%0A`;
    summary += `• Total de Registros: ${reportData.summary.total_records}%0A`;
    summary += `• Total de Horas: ${reportData.summary.total_hours}h%0A`;
    
    // Adicionar faturamento se tiver valor da hora
    if (hourlyRate && parseFloat(hourlyRate) > 0) {
      const totalValue = reportData.summary.total_hours * parseFloat(hourlyRate);
      summary += `• Valor da Hora: R$ ${parseFloat(hourlyRate).toFixed(2)}%0A`;
      summary += `• *TOTAL A PAGAR: R$ ${totalValue.toFixed(2)}*%0A`;
    }
    
    summary += `%0A`;
    
    // Top 3 por horas (se disponível)
    const top3 = reportData.data
      .filter(r => r.hours_worked > 0)
      .sort((a, b) => parseFloat(b.hours_worked) - parseFloat(a.hours_worked))
      .slice(0, 3);
    
    if (top3.length > 0) {
      summary += `🏆 *TOP 3 O.S. POR HORAS:*%0A`;
      top3.forEach((os, idx) => {
        summary += `${idx + 1}. O.S. #${os.order_number} - ${os.hours_worked}h (${os.machine})%0A`;
      });
    }
    
    summary += `%0A_Gerado por Impulso Digital_`;
    
    window.open(`https://wa.me/?text=${summary}`, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };
  
  // Formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };
  
  // Calcular total do faturamento
  const calculateTotalPayment = () => {
    if (!reportData || !hourlyRate || parseFloat(hourlyRate) <= 0) return 0;
    return reportData.summary.total_hours * parseFloat(hourlyRate);
  };

  // ========== Manutenção ==========
  const generateMaintenanceReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/maintenance-orders`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(maintFilters)
      });

      if (response.ok) {
        const data = await response.json();
        setMaintenanceData(data);
        toast.success(`Relatório gerado: ${data.summary.total_records} registros`);
      } else {
        toast.error('Erro ao gerar relatório de manutenção');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const getMaintenancePDFUrl = () => {
    const params = new URLSearchParams();
    params.append('tenant_id', selectedTenantId);
    if (maintFilters.date_from) params.append('date_from', maintFilters.date_from);
    if (maintFilters.date_to) params.append('date_to', maintFilters.date_to);
    if (maintFilters.status) params.append('status', maintFilters.status);
    if (maintFilters.machine_id) params.append('machine_id', maintFilters.machine_id);
    if (maintFilters.technician_id) params.append('technician_id', maintFilters.technician_id);
    if (maintFilters.maintenance_type) params.append('maintenance_type', maintFilters.maintenance_type);
    const userName = localStorage.getItem('userName') || 'Responsável';
    params.append('issued_by_name', userName);
    return `${API_URL}/api/reports/maintenance-pdf?${params.toString()}`;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Relatórios</h1>
        <p className="text-gray-400 mt-1">Exporte dados de ordens de serviço</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#111] border border-gray-800 rounded-xl p-1" data-testid="report-tabs">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'general'
              ? 'bg-yellow-500 text-black'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="tab-general"
        >
          <FileText size={16} />
          O.S. Geral
        </button>
        <button
          onClick={() => setActiveTab('maintenance')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'maintenance'
              ? 'bg-yellow-500 text-black'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid="tab-maintenance"
        >
          <Wrench size={16} />
          O.S. Manutenção
        </button>
      </div>

      {/* ========== TAB: GENERAL (existing content) ========== */}
      {activeTab === 'general' && (
        <>
      {/* Relatórios Rápidos */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={20} className="text-yellow-500" />
          <h2 className="text-lg font-semibold text-white">Relatórios Rápidos</h2>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* PDF Diário */}
          <button
            onClick={() => setPdfModal({
              url: `${API_URL}/api/reports/export-pdf?date_from=${new Date().toISOString().split('T')[0]}&date_to=${new Date().toISOString().split('T')[0]}&tenant_id=${selectedTenantId}${hourlyRate ? `&hourly_rate=${hourlyRate}` : ''}&issued_by_name=${encodeURIComponent(localStorage.getItem('userName') || 'Responsável')}`,
              filename: `relatorio_os_diario_${new Date().toISOString().split('T')[0]}.pdf`,
              title: 'Relatório Diário (PDF)',
            })}
            className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-red-500/50 transition-all flex items-center gap-3 group"
            data-testid="quick-pdf-daily"
          >
            <div className="bg-red-500/10 p-2.5 rounded-lg group-hover:bg-red-500/20 transition-colors">
              <FileDown size={18} className="text-red-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">PDF Diário</p>
              <p className="text-gray-500 text-xs">Hoje</p>
            </div>
          </button>

          {/* PDF Semanal */}
          <button
            onClick={() => setPdfModal({
              url: `${API_URL}/api/reports/export-pdf?date_from=${new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}&date_to=${new Date().toISOString().split('T')[0]}&tenant_id=${selectedTenantId}${hourlyRate ? `&hourly_rate=${hourlyRate}` : ''}&issued_by_name=${encodeURIComponent(localStorage.getItem('userName') || 'Responsável')}`,
              filename: `relatorio_os_semanal_${new Date().toISOString().split('T')[0]}.pdf`,
              title: 'Relatório Semanal (PDF)',
            })}
            className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-red-500/50 transition-all flex items-center gap-3 group"
            data-testid="quick-pdf-weekly"
          >
            <div className="bg-red-500/10 p-2.5 rounded-lg group-hover:bg-red-500/20 transition-colors">
              <FileDown size={18} className="text-red-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">PDF Semanal</p>
              <p className="text-gray-500 text-xs">Últimos 7 dias</p>
            </div>
          </button>

          {/* WhatsApp Diário */}
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const [y, m, d] = today.split('-');
              const rateParam = hourlyRate ? `&hourly_rate=${hourlyRate}` : '';
              const pdfUrl = encodeURIComponent(`${API_URL}/api/reports/export-pdf?date_from=${today}&date_to=${today}&tenant_id=${selectedTenantId}${rateParam}`);
              let message = `📊 *Relatório Diário - Impulso Digital*%0A📅 Data: ${d}/${m}/${y}`;
              if (hourlyRate && parseFloat(hourlyRate) > 0) {
                message += `%0A💰 Valor/Hora: R$ ${parseFloat(hourlyRate).toFixed(2)}`;
              }
              message += `%0A%0A📥 Baixe o PDF:%0A${pdfUrl}`;
              window.open(`https://wa.me/?text=${message}`, '_blank');
              toast.success('Abrindo WhatsApp...');
            }}
            className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-green-500/50 transition-all flex items-center gap-3 group"
            data-testid="quick-whatsapp-daily"
          >
            <div className="bg-green-500/10 p-2.5 rounded-lg group-hover:bg-green-500/20 transition-colors">
              <MessageCircle size={18} className="text-green-400" />
            </div>
            <div className="text-left">
              <p className="text-white text-sm font-medium">WhatsApp Diário</p>
              <p className="text-gray-500 text-xs">Compartilhar hoje</p>
            </div>
          </button>

          {/* WhatsApp Semanal */}
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
              const [ty, tm, td] = today.split('-');
              const [wy, wm, wd] = weekAgo.split('-');
              const rateParam = hourlyRate ? `&hourly_rate=${hourlyRate}` : '';
              const pdfUrl = encodeURIComponent(`${API_URL}/api/reports/export-pdf?date_from=${weekAgo}&date_to=${today}&tenant_id=${selectedTenantId}${rateParam}`);
              let message = `📊 *Relatório Semanal - Impulso Digital*%0A📅 Período: ${wd}/${wm}/${wy} a ${td}/${tm}/${ty}`;
              if (hourlyRate && parseFloat(hourlyRate) > 0) {
                message += `%0A💰 Valor/Hora: R$ ${parseFloat(hourlyRate).toFixed(2)}`;
              }
              message += `%0A%0A📥 Baixe o PDF:%0A${pdfUrl}`;
              window.open(`https://wa.me/?text=${message}`, '_blank');
              toast.success('Abrindo WhatsApp...');
            }}
            className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-green-500/50 transition-all flex items-center gap-3 group"
            data-testid="quick-whatsapp-weekly"
          >
            <div className="bg-emerald-500/10 p-2.5 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
              <MessageCircle size={18} className="text-emerald-400" />
            </div>
            <div className="text-left">
              <p className="text-white text-sm font-medium">WhatsApp Semanal</p>
              <p className="text-gray-500 text-xs">Compartilhar semana</p>
            </div>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-yellow-500" />
          <h2 className="text-lg font-semibold text-white">Filtros do Relatório</h2>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Período */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Data Inicial *</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-date-from"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Data Final *</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-date-to"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-status"
            >
              <option value="all">Todos</option>
              <option value="open">Abertas</option>
              <option value="closed">Fechadas</option>
            </select>
          </div>

          {/* Tipo de Operação */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Tipo de O.S.</label>
            <select
              value={filters.tipo_os}
              onChange={(e) => setFilters({...filters, tipo_os: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-tipo-os"
            >
              <option value="">Todas</option>
              <option value="Operacional">Operacional</option>
              <option value="Manutenção Preventiva">Manutenção Preventiva</option>
              <option value="Manutenção Corretiva">Manutenção Corretiva</option>
              <option value="Abastecimento">Abastecimento</option>
            </select>
          </div>

          {/* Tipo de Operação */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Tipo de Operação</label>
            <select
              value={filters.operation_type}
              onChange={(e) => setFilters({...filters, operation_type: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-operation-type"
            >
              <option value="">Todos</option>
              {operationTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Local */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Local</label>
            <select
              value={filters.location_id}
              onChange={(e) => setFilters({...filters, location_id: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-location"
            >
              <option value="">Todos</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.full_name}</option>
              ))}
            </select>
          </div>

          {/* Máquina */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Máquina</label>
            <select
              value={filters.machine_id}
              onChange={(e) => setFilters({...filters, machine_id: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-machine"
            >
              <option value="">Todas</option>
              {machines.filter(m => m.status === 'active').map(machine => (
                <option key={machine.id} value={machine.id}>
                  {machine.brand} {machine.model} - {machine.serial_number}
                </option>
              ))}
            </select>
          </div>

          {/* Operador */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Operador</label>
            <select
              value={filters.operator_id}
              onChange={(e) => setFilters({...filters, operator_id: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              data-testid="filter-operator"
            >
              <option value="">Todos</option>
              {operators.filter(o => o.status === 'active').map(operator => (
                <option key={operator.id} value={operator.id}>{operator.name}</option>
              ))}
            </select>
          </div>

          {/* Botão Gerar */}
          <div className="flex items-end">
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <FileText size={18} />
                  Gerar Relatório
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Campo de Valor da Hora - Faturamento (apenas admin e gestor) */}
        {canSeeBilling && (
          <div className="mt-4 p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <DollarSign size={20} className="text-green-500" />
                <span className="text-green-400 font-medium">Cálculo de Faturamento</span>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400 whitespace-nowrap">Valor da Hora:</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    className="w-32 pl-10 pr-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-green-500 focus:outline-none text-right"
                    data-testid="hourly-rate-input"
                  />
                </div>
              </div>
              {hourlyRate && parseFloat(hourlyRate) > 0 && reportData && (
                <div className="bg-green-500/10 px-4 py-2 rounded-lg">
                  <span className="text-green-400 font-bold text-lg">
                    Total: {formatCurrency(calculateTotalPayment())}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Resultado do Relatório */}
      {reportData && (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          {/* Header do relatório */}
          <div className="p-4 lg:p-6 border-b border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Table size={20} className="text-yellow-500" />
                Resultado do Relatório
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Período: {formatDate(filters.date_from)} a {formatDate(filters.date_to)}
              </p>
            </div>
            
            <div className="flex flex-col gap-3 w-full sm:w-auto">
              {/* Summary */}
              <div className="bg-yellow-500/10 px-4 py-2 rounded-lg text-center">
                <p className="text-yellow-500 text-xs">Total de Horas</p>
                <p className="text-yellow-500 font-bold text-xl flex items-center justify-center gap-1">
                  <Clock size={16} />
                  {reportData.summary.total_hours}h
                </p>
              </div>

              {/* Type breakdown cards */}
              {reportData.type_breakdown && reportData.type_breakdown.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {reportData.type_breakdown.map((tb, i) => {
                    const colors = {
                      'Operacional': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                      'Manutenção Preventiva': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
                      'Manutenção Corretiva': 'bg-red-500/10 text-red-400 border-red-500/20',
                      'Abastecimento': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                      'Não Informado': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
                    };
                    const cls = colors[tb.tipo] || colors['Não Informado'];
                    return (
                      <div key={i} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${cls}`} data-testid={`type-breakdown-${i}`}>
                        {tb.tipo}: {tb.count} {tb.hours > 0 ? `(${tb.hours}h)` : ''}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Export buttons - Grid layout */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                {/* CSV Buttons */}
                <button
                  onClick={exportToCSV}
                  className="px-3 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                  data-testid="export-csv-btn"
                >
                  <FileSpreadsheet size={16} />
                  CSV
                </button>
                <button
                  onClick={() => setPdfModal({
                    url: getPDFUrl(),
                    filename: `relatorio_os_${filters.date_from}_a_${filters.date_to}.pdf`,
                    title: 'Relatório de O.S. (PDF)',
                  })}
                  className="px-3 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                  data-testid="export-pdf-btn"
                >
                  <FileDown size={16} />
                  PDF
                </button>
                
                {/* WhatsApp Buttons */}
                <button
                  onClick={shareCSVLink}
                  className="px-3 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                  data-testid="share-link-btn"
                >
                  <MessageCircle size={16} />
                  Link
                </button>
                <button
                  onClick={shareReportSummary}
                  className="px-3 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                  data-testid="share-summary-btn"
                >
                  <Share2 size={16} />
                  Resumo
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Cards View */}
          <div className="block lg:hidden space-y-3 p-4">
            {reportData.data.length > 0 ? (
              reportData.data.map((row, index) => (
                <div key={index} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-yellow-500 font-bold text-lg">
                        O.S. #{row.order_number || '-'}
                      </span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded ${
                        row.status === 'Fechada' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {row.status}
                      </span>
                    </div>
                    <span className="text-white text-sm">{formatDate(row.date)}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Máquina:</span>
                      <p className="text-gray-300 truncate">{row.machine}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Operador:</span>
                      <p className="text-gray-300">{row.operator}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Local:</span>
                      <p className="text-gray-300 truncate">{row.location || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Tipo O.S.:</span>
                      <p className="text-gray-300">{row.tipo_os || 'Não Informado'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Tipo Op.:</span>
                      <p className="text-gray-300">{row.operation_type || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-800">
                    <div className="flex gap-4 text-sm">
                      <span className="text-gray-400">
                        Inicial: <span className="text-white font-mono">{row.start_hour_meter}</span>
                      </span>
                      <span className="text-gray-400">
                        Final: <span className="text-white font-mono">{row.end_hour_meter || '-'}</span>
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-yellow-500 font-bold text-lg">
                        {row.hours_worked}h
                      </span>
                      {canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 && row.hours_worked && (
                        <p className="text-green-400 text-sm font-semibold">
                          {formatCurrency(parseFloat(row.hours_worked) * parseFloat(hourlyRate))}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                Nenhum registro encontrado para os filtros selecionados
              </div>
            )}
            
            {/* Mobile Total */}
            {reportData.data.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-sm">Total ({reportData.summary.total_records} registros)</p>
                <p className="text-yellow-500 font-bold text-2xl">{reportData.summary.total_hours}h</p>
                {canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 && (
                  <p className="text-green-400 font-bold text-xl mt-1">
                    {formatCurrency(calculateTotalPayment())}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">O.S. Nº</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo O.S.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Local</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo Op.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Máquina</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Operador</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Hor. Ini.</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Hor. Fin.</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Horas</th>
                  {canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-green-400 uppercase">Valor R$</th>
                  )}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {reportData.data.length > 0 ? (
                  reportData.data.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-white font-semibold">
                        {row.order_number ? `#${row.order_number}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">{formatDate(row.date)}</td>
                      <td className="px-4 py-3 text-sm">
                        {(() => {
                          const colors = {
                            'Operacional': 'bg-blue-500/20 text-blue-400',
                            'Manutenção Preventiva': 'bg-yellow-500/20 text-yellow-400',
                            'Manutenção Corretiva': 'bg-red-500/20 text-red-400',
                            'Abastecimento': 'bg-emerald-500/20 text-emerald-400',
                          };
                          const tipo = row.tipo_os || 'Não Informado';
                          const cls = colors[tipo] || 'bg-gray-500/20 text-gray-400';
                          return <span className={`text-xs px-2 py-1 rounded ${cls}`}>{tipo}</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300 max-w-[200px] truncate" title={row.location}>
                        {row.location}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{row.operation_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-300 max-w-[150px] truncate" title={row.machine}>
                        {row.machine}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{row.operator}</td>
                      <td className="px-4 py-3 text-sm text-white text-right font-mono">{row.start_hour_meter}</td>
                      <td className="px-4 py-3 text-sm text-white text-right font-mono">
                        {row.end_hour_meter || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-yellow-500 text-right font-mono font-semibold">
                        {row.hours_worked}h
                      </td>
                      {canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 && (
                        <td className="px-4 py-3 text-sm text-green-400 text-right font-mono font-semibold">
                          {row.hours_worked ? formatCurrency(parseFloat(row.hours_worked) * parseFloat(hourlyRate)) : '-'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded ${
                          row.status === 'Fechada' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 ? 12 : 11} className="px-4 py-8 text-center text-gray-500">
                      Nenhum registro encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
              </tbody>
              {reportData.data.length > 0 && (
                <tfoot className="bg-gray-900/50">
                  <tr>
                    <td colSpan={9} className="px-4 py-3 text-right text-sm font-semibold text-white">
                      Total ({reportData.summary.total_records} registros):
                    </td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-yellow-500 font-mono">
                      {reportData.summary.total_hours}h
                    </td>
                    {canSeeBilling && hourlyRate && parseFloat(hourlyRate) > 0 && (
                      <td className="px-4 py-3 text-right text-lg font-bold text-green-400 font-mono">
                        {formatCurrency(calculateTotalPayment())}
                      </td>
                    )}
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Horas Não Operacionais (bloco separado, ao final) */}
      {reportData && reportData.nao_operacional && (
        <div className="bg-[#111] border border-purple-500/30 rounded-xl overflow-hidden" data-testid="non-op-report-section">
          <div className="p-4 lg:p-6 border-b border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock size={20} className="text-purple-400" />
                Horas Não Operacionais
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Eventos fora do contexto de O.S. (embarque, translado, etc.) no período
              </p>
            </div>
            <div className="bg-purple-500/10 px-4 py-2 rounded-lg text-center">
              <p className="text-purple-400 text-xs">Total Não Operacional</p>
              <p className="text-purple-400 font-bold text-xl flex items-center justify-center gap-1">
                <Clock size={16} />
                {reportData.nao_operacional.total_hours}h
              </p>
            </div>
          </div>

          {reportData.nao_operacional.data.length > 0 ? (
            <>
              {/* Mobile */}
              <div className="block lg:hidden space-y-3 p-4">
                {reportData.nao_operacional.data.map((row, index) => (
                  <div key={index} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300">{row.categoria}</span>
                      <span className="text-purple-400 font-bold">{row.horas_nao_operacionais}h</span>
                    </div>
                    <p className="text-gray-300 text-sm truncate">{row.machine}</p>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>{formatDate(row.date)}</span>
                      <span className="font-mono">{row.horimetro_inicial} → {row.horimetro_final}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full" data-testid="non-op-report-table">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Máquina</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Hor. Ini.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Hor. Fin.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Horas</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Registrado por</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {reportData.nao_operacional.data.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-900/30">
                        <td className="px-4 py-3 text-sm text-white">{formatDate(row.date)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300">{row.categoria}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300 max-w-[200px] truncate" title={row.machine}>{row.machine}</td>
                        <td className="px-4 py-3 text-sm text-white text-right font-mono">{row.horimetro_inicial}</td>
                        <td className="px-4 py-3 text-sm text-white text-right font-mono">{row.horimetro_final}</td>
                        <td className="px-4 py-3 text-sm text-purple-400 text-right font-mono font-semibold">{row.horas_nao_operacionais}h</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{row.registrado_por}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-900/50">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-right text-sm font-semibold text-white">
                        Total ({reportData.nao_operacional.total_records} lançamentos):
                      </td>
                      <td className="px-4 py-3 text-right text-lg font-bold text-purple-400 font-mono">
                        {reportData.nao_operacional.total_hours}h
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Nenhum lançamento de horas não operacionais no período
            </div>
          )}
        </div>
      )}

        </>
      )}

      {/* ========== TAB: MAINTENANCE ========== */}
      {activeTab === 'maintenance' && (
        <>
          {/* Filtros de Manutenção */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wrench size={20} className="text-yellow-500" />
              <h2 className="text-lg font-semibold text-white">Filtros - O.S. de Manutenção</h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Data Inicial</label>
                <input
                  type="date"
                  value={maintFilters.date_from}
                  onChange={(e) => setMaintFilters({...maintFilters, date_from: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-date-from"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Data Final</label>
                <input
                  type="date"
                  value={maintFilters.date_to}
                  onChange={(e) => setMaintFilters({...maintFilters, date_to: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-date-to"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Máquina</label>
                <select
                  value={maintFilters.machine_id}
                  onChange={(e) => setMaintFilters({...maintFilters, machine_id: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-machine"
                >
                  <option value="">Todas</option>
                  {machines.filter(m => m.status === 'active').map(machine => (
                    <option key={machine.id} value={machine.id}>
                      {machine.brand} {machine.model} - {machine.serial_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Técnico Responsável</label>
                <select
                  value={maintFilters.technician_id}
                  onChange={(e) => setMaintFilters({...maintFilters, technician_id: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-technician"
                >
                  <option value="">Todos</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>{tech.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Tipo de Manutenção</label>
                <select
                  value={maintFilters.maintenance_type}
                  onChange={(e) => setMaintFilters({...maintFilters, maintenance_type: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-type"
                >
                  <option value="">Todos</option>
                  <option value="preventiva">Preventiva</option>
                  <option value="corretiva">Corretiva</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Status da O.S.</label>
                <select
                  value={maintFilters.status}
                  onChange={(e) => setMaintFilters({...maintFilters, status: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                  data-testid="maint-filter-status"
                >
                  <option value="">Todas</option>
                  <option value="open">Aberta</option>
                  <option value="closed">Fechada</option>
                  <option value="pending">Pendente</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                onClick={generateMaintenanceReport}
                disabled={loading}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-700 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="generate-maintenance-report-btn"
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> Gerando...</>
                ) : (
                  <><Wrench size={18} /> Gerar Relatório</>
                )}
              </button>
            </div>
          </div>

          {/* Resultado do Relatório de Manutenção */}
          {maintenanceData && (
            <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-4 lg:p-6 border-b border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Wrench size={20} className="text-yellow-500" />
                    Relatório de O.S. de Manutenção
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    {maintenanceData.summary.total_records} registro(s) encontrado(s)
                  </p>
                </div>

                <div className="flex flex-col gap-3 w-full sm:w-auto">
                  <div className="bg-yellow-500/10 px-4 py-2 rounded-lg text-center">
                    <p className="text-yellow-500 text-xs">Total de Horas</p>
                    <p className="text-yellow-500 font-bold text-xl flex items-center justify-center gap-1">
                      <Clock size={16} />
                      {maintenanceData.summary.total_hours}h
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={() => setPdfModal({
                        url: getMaintenancePDFUrl(),
                        filename: `relatorio_manutencao_${new Date().toISOString().split('T')[0]}.pdf`,
                        title: 'Relatório de Manutenção (PDF)',
                      })}
                      className="px-3 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                      data-testid="export-maintenance-pdf-btn"
                    >
                      <FileDown size={16} />
                      Exportar PDF
                    </button>
                    <button
                      onClick={() => {
                        const records = maintenanceData.summary.total_records;
                        const hours = maintenanceData.summary.total_hours;
                        const pdfUrl = getMaintenancePDFUrl();
                        const text = `Relatório de Manutenção\n${records} registros | ${hours}h\n\nPDF: ${pdfUrl}`;
                        const encoded = encodeURIComponent(text);
                        window.location.href = `https://wa.me/?text=${encoded}`;
                      }}
                      className="px-3 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs font-medium"
                      data-testid="share-maintenance-whatsapp-btn"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="block lg:hidden space-y-3 p-4">
                {maintenanceData.data.length > 0 ? (
                  maintenanceData.data.map((row, index) => (
                    <div key={index} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span className="text-yellow-500 font-bold text-lg">
                            O.S. #{row.order_number || '-'}
                          </span>
                          <span className={`ml-2 text-xs px-2 py-1 rounded ${
                            row.status === 'Fechada' ? 'bg-green-500/20 text-green-400' :
                            row.status === 'Pendente' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>
                            {row.status}
                          </span>
                        </div>
                        <span className="text-white text-sm">{formatDate(row.date)}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Máquina:</span>
                          <p className="text-gray-300 truncate">{row.machine}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Técnico:</span>
                          <p className="text-gray-300">{row.technician}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Operador:</span>
                          <p className="text-gray-300">{row.operator}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Tipo:</span>
                          <p className="text-gray-300">{row.maintenance_type}</p>
                        </div>
                      </div>

                      {row.maintenance_items && (
                        <div className="mt-2 text-sm">
                          <span className="text-gray-500">Itens:</span>
                          <p className="text-gray-300 text-xs mt-1 whitespace-pre-line line-clamp-3">{row.maintenance_items}</p>
                        </div>
                      )}

                      <div className="flex justify-end items-center mt-3 pt-3 border-t border-gray-800">
                        <span className="text-yellow-500 font-bold text-lg">{row.hours_worked}h</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    Nenhuma O.S. de manutenção encontrada
                  </div>
                )}
              </div>

              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full" data-testid="maintenance-report-table">
                  <thead className="bg-gray-900/50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">O.S.</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Máquina</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Técnico</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Operador</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo Man.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">Horas</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">Itens/Observações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {maintenanceData.data.length > 0 ? (
                      maintenanceData.data.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-900/30">
                          <td className="px-3 py-3 text-sm text-white font-semibold">
                            {row.order_number ? `#${row.order_number}` : '-'}
                          </td>
                          <td className="px-3 py-3 text-sm text-white">{formatDate(row.date)}</td>
                          <td className="px-3 py-3 text-sm text-gray-300 max-w-[150px] truncate" title={row.machine}>
                            {row.machine}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-300">{row.technician}</td>
                          <td className="px-3 py-3 text-sm text-gray-300">{row.operator}</td>
                          <td className="px-3 py-3 text-sm">
                            <span className={`text-xs px-2 py-1 rounded ${
                              row.maintenance_type === 'preventiva' ? 'bg-blue-500/20 text-blue-400' :
                              row.maintenance_type === 'corretiva' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {row.maintenance_type}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-yellow-500 text-right font-mono font-semibold">
                            {row.hours_worked}h
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs px-2 py-1 rounded ${
                              row.status === 'Fechada' ? 'bg-green-500/20 text-green-400' :
                              row.status === 'Pendente' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-orange-500/20 text-orange-400'
                            }`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-400 max-w-[250px]">
                            <p className="truncate" title={row.maintenance_items || row.observations}>
                              {row.maintenance_items || row.observations || '-'}
                            </p>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          Nenhuma O.S. de manutenção encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {maintenanceData.data.length > 0 && (
                    <tfoot className="bg-gray-900/50">
                      <tr>
                        <td colSpan={6} className="px-3 py-3 text-right text-sm font-semibold text-white">
                          Total ({maintenanceData.summary.total_records} registros):
                        </td>
                        <td className="px-3 py-3 text-right text-lg font-bold text-yellow-500 font-mono">
                          {maintenanceData.summary.total_hours}h
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <PdfActionsModal
        open={!!pdfModal}
        onClose={() => setPdfModal(null)}
        url={pdfModal?.url}
        filename={pdfModal?.filename}
        title={pdfModal?.title}
      />
    </div>
  );
}

